type CreditLimitAccount = {
  contractNumber?: unknown;
  creditLimit?: unknown;
};

type DollarLimit = {
  code: string | number;
};

type CreditLimitValue = string | number | null | undefined;
const NO_LIMIT_CODE = 999999999;
const RESTRICTED_DAILY_LIMIT_CODES = new Set(['20000', '25000', '50000', '60000', String(NO_LIMIT_CODE)]);

const parseAmount = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }

  const amount = Number(value.replace(/[$,]/g, '').trim());
  return Number.isFinite(amount) ? amount : undefined;
};

/** Removes daily-limit choices disallowed for Order Card and Maintain Card. */
export const filterRestrictedDailyDollarLimits = <T extends DollarLimit>(
  dailyDollarLimits: readonly T[] | undefined
): T[] | undefined => dailyDollarLimits?.filter((limit) => !RESTRICTED_DAILY_LIMIT_CODES.has(String(limit.code)));

const getSelectedContractNumbers = (selection: unknown): string[] => {
  if (typeof selection === 'string') {
    return [selection];
  }

  if (Array.isArray(selection)) {
    return selection.flatMap(getSelectedContractNumbers);
  }

  if (typeof selection !== 'object' || selection === null) {
    return [];
  }

  if ('contractNumber' in selection && typeof selection.contractNumber === 'string') {
    return [selection.contractNumber];
  }

  return 'value' in selection ? getSelectedContractNumbers(selection.value) : [];
};

/** Returns the strictest positive credit limit for the selected account(s). */
export const getEffectiveCreditLimit = (
  accounts: readonly unknown[] = [],
  contractNumbers?: unknown
): number | undefined => {
  const selectedContractNumbers = getSelectedContractNumbers(contractNumbers);
  const selectedAccounts = accounts
    .filter(
      (account): account is CreditLimitAccount =>
        typeof account === 'object' && account !== null && 'contractNumber' in account
    )
    .filter(
      (account) =>
        typeof account.contractNumber === 'string' && selectedContractNumbers.includes(account.contractNumber)
    );

  if (selectedAccounts.length === 0) {
    return undefined;
  }

  const accountCreditLimits = selectedAccounts.map((account) => parseAmount(account.creditLimit));
  const positiveCreditLimits = accountCreditLimits.filter(
    (creditLimit): creditLimit is number => creditLimit !== undefined && creditLimit > 0
  );

  return positiveCreditLimits.length > 0 ? Math.min(...positiveCreditLimits) : 0;
};

/** Filters daily-limit choices; zero, unknown, or the No Limit sentinel means no filtering. */
export const filterDailyDollarLimitsByCreditLimit = <T extends DollarLimit>(
  dailyDollarLimits: readonly T[] | undefined,
  creditLimit: CreditLimitValue
): T[] | undefined => {
  if (!dailyDollarLimits) {
    return undefined;
  }

  const numericCreditLimit = parseAmount(creditLimit);
  if (numericCreditLimit === undefined || numericCreditLimit <= 0 || numericCreditLimit === NO_LIMIT_CODE) {
    return [...dailyDollarLimits];
  }

  return dailyDollarLimits.filter((limit) => {
    const dailyLimit = parseAmount(limit.code);
    return dailyLimit !== undefined && dailyLimit !== NO_LIMIT_CODE && dailyLimit <= numericCreditLimit;
  });
};

/** Normalizes a Favourite daily limit to the highest valid option under its credit limit. */
export const capDailyLimitToCreditLimit = <T extends DollarLimit>(
  dailyLimit: CreditLimitValue,
  dailyDollarLimits: readonly T[] | undefined,
  creditLimit: CreditLimitValue
): CreditLimitValue => {
  const selectedDailyLimit = parseAmount(dailyLimit);
  const numericCreditLimit = parseAmount(creditLimit);

  if (
    selectedDailyLimit === undefined ||
    numericCreditLimit === undefined ||
    numericCreditLimit <= 0 ||
    numericCreditLimit === NO_LIMIT_CODE ||
    !dailyDollarLimits?.length
  ) {
    return dailyLimit;
  }

  const availableLimits = filterDailyDollarLimitsByCreditLimit(dailyDollarLimits, numericCreditLimit) ?? [];
  const maxDailyLimit = Math.min(selectedDailyLimit, numericCreditLimit);
  const highestAllowedLimit = availableLimits.reduce<number | undefined>((highest, option) => {
    const optionValue = parseAmount(option.code);
    if (optionValue === undefined || optionValue > maxDailyLimit) {
      return highest;
    }
    return highest === undefined || optionValue > highest ? optionValue : highest;
  }, undefined);

  return highestAllowedLimit === undefined ? undefined : String(highestAllowedLimit);
};

/** Caps a Favourite daily limit at the highest option available in the Daily Limit dropdown. */
export const capDailyLimitToMaximumOption = <T extends DollarLimit>(
  dailyLimit: CreditLimitValue,
  availableDailyLimits: readonly T[] | undefined
): CreditLimitValue => {
  const selectedDailyLimit = parseAmount(dailyLimit);
  if (selectedDailyLimit === undefined || !availableDailyLimits?.length) {
    return dailyLimit;
  }

  const maximumAvailableLimit = availableDailyLimits.reduce<number | undefined>((maximum, option) => {
    const optionValue = parseAmount(option.code);
    return optionValue === undefined || (maximum !== undefined && optionValue <= maximum) ? maximum : optionValue;
  }, undefined);

  return maximumAvailableLimit !== undefined && selectedDailyLimit > maximumAvailableLimit
    ? String(maximumAvailableLimit)
    : dailyLimit;
};
